import React, { useMemo } from 'react'
import { Box, Typography } from '@mui/material'
import { Input } from '@/design-system'
import {
  getWizardColors,
  getWizardFrames,
  getWizardMaterials,
  getWizardScenes,
} from '../../catalog'
import { FRAME_NONE } from '../../types'
import type { GalleryPrivacy, WizardDraft, WizardStepId } from '../../types'
import { FieldLabel } from '../StepCopy'

export interface ReviewSummaryProps {
  draft: WizardDraft
  onTitleChange: (title: string) => void
  onPrivacyChange: (p: GalleryPrivacy) => void
  onEditStep?: (step: WizardStepId) => void
}

export function ReviewSummary({
  draft,
  onTitleChange,
  onPrivacyChange,
  onEditStep,
}: ReviewSummaryProps) {
  const scene = useMemo(
    () => getWizardScenes(24).find((s) => s.id === draft.sceneId),
    [draft.sceneId],
  )
  const frame = useMemo(() => {
    if (draft.frameId == null || draft.frameId === FRAME_NONE) return null
    return getWizardFrames(24).find((f) => f.id === draft.frameId)
  }, [draft.frameId])
  const material = useMemo(
    () => getWizardMaterials(24).find((m) => m.id === draft.materialId),
    [draft.materialId],
  )
  const color = useMemo(
    () => getWizardColors(24).find((c) => c.id === draft.colorId),
    [draft.colorId],
  )
  const printCount = draft.slots.filter((s) => s.photoId && !s.excluded).length
  const slotTotal = draft.slots.length

  const rows: { label: string; value: string; step: WizardStepId; swatch?: string }[] = [
    { label: 'Scene', value: scene?.name || '—', step: 'scene' },
    {
      label: 'Frame',
      value: frame?.name || 'None',
      step: 'frame',
    },
    {
      label: 'Color',
      value: color?.name || '—',
      step: 'color',
      swatch: color?.hex,
    },
    { label: 'Material', value: material?.name || '—', step: 'material' },
    {
      label: 'Prints',
      value: `${printCount} of ${slotTotal} slots`,
      step: 'prints',
    },
    {
      label: 'Matting',
      value: `${Math.round(draft.matWidth * 100)}%`,
      step: 'matting',
      swatch: draft.matColor,
    },
  ]

  return (
    <Box display='flex' flexDirection={{ xs: 'column', md: 'row' }} gap={4}>
      <Box flex={1.35} minWidth={0}>
        <Box
          sx={{
            borderRadius: 20,
            overflow: 'hidden',
            aspectRatio: '16 / 10',
            bgcolor: 'var(--ds-hover)',
            border: '1px solid var(--ds-border)',
          }}
        >
          {scene?.thumbUrl ? (
            <Box
              component='img'
              src={scene.thumbUrl}
              alt=''
              sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
          ) : null}
        </Box>
      </Box>

      <Box flex={1} minWidth={0}>
        <FieldLabel>Gallery name</FieldLabel>
        <Input
          value={draft.galleryTitle}
          onChange={(e) => onTitleChange(e.target.value)}
          placeholder='Gallery name'
          sx={{ mb: 3, width: '100%' }}
        />

        <FieldLabel>Privacy</FieldLabel>
        <Box
          display='inline-flex'
          p={0.5}
          mb={3.5}
          borderRadius='var(--ds-radius-pill)'
          bgcolor='var(--ds-hover)'
          gap={0.5}
          role='group'
          aria-label='Privacy'
        >
          {(['private', 'public'] as GalleryPrivacy[]).map((p) => {
            const active = draft.privacy === p
            return (
              <Box
                key={p}
                component='button'
                type='button'
                onClick={() => onPrivacyChange(p)}
                aria-pressed={active}
                sx={{
                  all: 'unset',
                  cursor: 'pointer',
                  px: 2.25,
                  py: 1,
                  borderRadius: 'var(--ds-radius-pill)',
                  fontSize: 13,
                  fontWeight: 600,
                  letterSpacing: '-0.01em',
                  background: active ? 'var(--ds-inverse)' : 'transparent',
                  color: active ? 'var(--ds-inverse-ink)' : 'var(--ds-ink-muted)',
                  transition: 'background var(--ds-duration-fast) var(--ds-ease)',
                  '&:focus-visible': {
                    outline: '2px solid var(--ds-ink)',
                    outlineOffset: 2,
                  },
                }}
              >
                {p === 'public' ? 'Public' : 'Private'}
              </Box>
            )
          })}
        </Box>

        <FieldLabel>Summary</FieldLabel>
        <Box>
          {rows.map((r) => (
            <Box
              key={r.label}
              display='flex'
              alignItems='center'
              justifyContent='space-between'
              gap={2}
              py={1.25}
              borderBottom='1px solid var(--ds-border)'
            >
              <Typography sx={{ fontSize: 13, color: 'var(--ds-ink-muted)' }}>{r.label}</Typography>
              <Box display='flex' alignItems='center' gap={1.25} minWidth={0}>
                {r.swatch && (
                  <Box
                    width={14}
                    height={14}
                    borderRadius='50%'
                    bgcolor={r.swatch}
                    border='1px solid rgba(17,17,17,0.1)'
                    flexShrink={0}
                  />
                )}
                <Typography
                  sx={{
                    fontSize: 13,
                    fontWeight: 500,
                    letterSpacing: '-0.01em',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {r.value}
                </Typography>
                {onEditStep && (
                  <Box
                    component='button'
                    type='button'
                    onClick={() => onEditStep(r.step)}
                    sx={{
                      all: 'unset',
                      cursor: 'pointer',
                      fontSize: 12,
                      fontWeight: 600,
                      color: 'var(--ds-ink-subtle)',
                      '&:hover': { color: 'var(--ds-ink)' },
                      '&:focus-visible': {
                        outline: '2px solid var(--ds-ink)',
                        outlineOffset: 2,
                      },
                    }}
                  >
                    Edit
                  </Box>
                )}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  )
}
